import { DsmGrid } from "@dsm-dcs/design-system-react";
import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { useFormContext } from "react-hook-form";
import { DairyV2InputContinuousGrowing, DairyV2Intervention } from "../../../../models/Intervention/DairyV2Intervention";
import { StageType } from "../../../../../../graphql/types";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { ExternalSource } from "../../../../models/Intervention/CattleIntervention";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { Spacer } from "../../../helpers/Spacer";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import EditModeButtons from "../../../helpers/EditModeButtons";

interface DairyV2InputExternalSourceComponentProps {
  formType: FormType;
  stageType: StageType;
  fieldItemPrefix: string;
  defaultStageData: DairyV2InputContinuousGrowing;
  isExternalEdit: boolean;
  setIsExternalEdit: Dispatch<SetStateAction<boolean>>;
  extSourceCancelHandler: () => void;
}

const DairyV2InputExternalSourceComponent: FC<DairyV2InputExternalSourceComponentProps> = ({
  formType,
  stageType,
  fieldItemPrefix,
  defaultStageData,
  isExternalEdit,
  setIsExternalEdit,
  extSourceCancelHandler,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const fc = useFormContext<DairyV2Intervention>();

  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const distanceUnit = unitLong(userUOM.unitTransportDistanceTerrestrial as string);

  const externalSourceUnitStyle = (defaultStageData?.externalSources?.length ?? 0) > 1
    ? classes.additionalEntriesBlockHolder
    : classes.additionalEntriesBlockHolderWithoutBorder;

  const externalSourceUnit = (item: ExternalSource, index: number) => {
    const fieldPrefix = `${fieldItemPrefix}.externalSources[${index}]`;

    return (
      <DsmGrid className={externalSourceUnitStyle}>
        {(defaultStageData?.externalSources?.length ?? 0) > 1 && <Spacer />}
        {!isExternalEdit ? (
          <>
            {isFieldFilled(fc.getValues(`${fieldPrefix}.numberAnimals`)) ? (
              <>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.numberAnimals`}
                  label={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE" })}
                  tooltip={intl.formatMessage({
                    id: stageType === StageType.ContinuousGrowing
                      ? `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.CONTINUOUS_GROWING.TOOLTIP`
                      : `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.TOOLTIP`,
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT` })}
                  baseline={item.numberAnimals}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalNumberAnimals`}
                label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE` })}
                adornment={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT` })}
                tooltip={intl.formatMessage({
                  id: stageType === StageType.ContinuousGrowing
                    ? `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.CONTINUOUS_GROWING.TOOLTIP`
                    : `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.TOOLTIP`,
                })}
                type="number"
                defaultValue={item.numberAnimals ?? ''}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.distanceTruck`)) ? (
              <>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.distanceTruck`}
                  label={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK" })}
                  tooltip={intl.formatMessage({
                    id: stageType === StageType.ContinuousGrowing
                      ? `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.CONTINUOUS_GROWING.TOOLTIP`
                      : `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.TOOLTIP`,
                  })}
                  type="number"
                  metricUnit={distanceUnit}
                  baseline={item.distanceTruck}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDistanceTruck`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK" })}
                adornment={distanceUnit}
                tooltip={intl.formatMessage({
                  id: stageType === StageType.ContinuousGrowing
                    ? `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.CONTINUOUS_GROWING.TOOLTIP`
                    : `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.TOOLTIP`,
                })}
                type="number"
                defaultValue={item.distanceTruck ?? ''}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.shrinkingRate`)) ? (
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.shrinkingRate`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS.TOOLTIP" })}
                type="number"
                metricUnit="%"
                baseline={item.shrinkingRate}
                displayTypeSelect={false}
                disabled
              />
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalShrinkingRate`}
                label={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS" })}
                tooltip={intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS.TOOLTIP" })}
                placeholder={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS.PLACEHOLDER` })}
                adornment="%"
                type="number"
                defaultValue={item.shrinkingRate ?? ''}
                disabled
              />
            )}
          </>
        ) : (
          <>
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.numberAnimals`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE` })}
              tooltip={intl.formatMessage({
                id: stageType === StageType.ContinuousGrowing
                  ? `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.CONTINUOUS_GROWING.TOOLTIP`
                  : `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.TOOLTIP`,
              })}
              type="number"
              metricUnit={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT` })}
              baseline={item.numberAnimals}
              disabled={false}
            />
            <Spacer />
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.distanceTruck`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK` })}
              tooltip={intl.formatMessage({
                id: stageType === StageType.ContinuousGrowing
                  ? `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.CONTINUOUS_GROWING.TOOLTIP`
                  : `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.TOOLTIP`,
              })}
              type="number"
              metricUnit={distanceUnit}
              baseline={item.distanceTruck}
              disabled={false}
            />
            <Spacer />
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.shrinkingRate`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS` })}
              tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS.TOOLTIP` })}
              type="number"
              metricUnit="%"
              baseline={item.shrinkingRate}
              disabled={false}
            />
          </>
        )}
      </DsmGrid>
    )
  }

  return (
    <div>
      <label htmlFor="internalSource" className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: 'SUSTELL.STAGE.DAIRY.INPUT.EXTERNAL_SOURCE' })}
      </label>
      {!!defaultStageData?.externalSources?.length &&
        <DsmGrid className={classes.dsmGridOneColumnInterventionWithBorder}>
          {(defaultStageData?.externalSources || [])
            ?.filter((item) => !!item.numberAnimals)
            ?.map((item, index) => (externalSourceUnit(item, index)))}
          {formType !== FormType.View &&
            <EditModeButtons
              isEditing={isExternalEdit}
              onEdit={() => setIsExternalEdit(true)}
              onCancel={extSourceCancelHandler}
            />}
        </DsmGrid>}
    </div>
  )
}

export default DairyV2InputExternalSourceComponent;
